import * as React from "react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";

/**
 * BrandInput Component
 *
 * EverReal branded input field with:
 * - Deep emerald background
 * - Gold border with focus glow
 * - Floating label animation
 * - Icon support (left/right)
 * - Error state styling
 */

export type BrandInputVariant = "default" | "filled" | "outlined";

export interface BrandInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  /**
   * Input label
   */
  label?: string;

  /**
   * Error message to display
   */
  error?: string;

  /**
   * Hint text to display below input
   */
  hint?: string;

  /**
   * Visual variant
   */
  variant?: BrandInputVariant;

  /**
   * Left icon
   */
  iconLeft?: React.ReactNode;

  /**
   * Right icon
   */
  iconRight?: React.ReactNode;

  /**
   * Input size
   */
  inputSize?: "sm" | "md" | "lg";

  /**
   * Container class name
   */
  containerClassName?: string;
}

const BrandInput = React.forwardRef<HTMLInputElement, BrandInputProps>(
  (
    {
      className,
      containerClassName,
      label,
      error,
      hint,
      variant = "default",
      iconLeft,
      iconRight,
      inputSize = "md",
      id,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const inputId = id || React.useId();
    const [isFocused, setIsFocused] = React.useState(false);

    // Variant-specific styles
    const variantStyles: Record<BrandInputVariant, string> = {
      default:
        "bg-white border-border-card focus:border-brand-primary focus:ring-brand-primary/20",
      filled:
        "bg-slate-50 border-transparent focus:border-brand-primary focus:ring-brand-primary/20",
      outlined:
        "bg-transparent border-border-card focus:border-brand-primary focus:ring-brand-primary/30",
    };

    // Size-specific styles
    const sizeStyles = {
      sm: "h-9 px-3 text-sm",
      md: "h-10 px-4 text-base",
      lg: "h-12 px-5 text-lg",
    };

    return (
      <div className={cn("w-full", containerClassName)}>
        {/* Static Label Above Input */}
        {label && (
          <Label
            htmlFor={inputId}
            className={cn(
              "block mb-2 text-sm font-medium text-text-primary",
              props.required &&
                "after:content-['*'] after:ml-0.5 after:text-red-400",
            )}
          >
            {label}
          </Label>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Left Icon */}
          {iconLeft && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none">
              {iconLeft}
            </div>
          )}

          {/* Input Field */}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              // Base styles
              "flex w-full rounded-md border transition-all duration-200",
              "text-text-primary placeholder:text-text-muted",
              "focus:outline-none focus:ring-2 focus-visible:outline-none",
              "disabled:cursor-not-allowed disabled:opacity-50",

              // Variant styles
              variantStyles[variant],

              // Size styles
              sizeStyles[inputSize],

              // Icon padding
              iconLeft && "pl-10",
              iconRight && "pr-10",

              // Error state
              error &&
                "border-red-500/50 focus:border-red-500 focus:ring-red-500/20",

              // Custom class
              className,
            )}
            placeholder={placeholder}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            {...props}
          />

          {/* Right Icon */}
          {iconRight && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none">
              {iconRight}
            </div>
          )}

          {/* Focus Glow Effect */}
          {isFocused && (
            <div className="absolute inset-0 -z-10 rounded-md blur-md bg-brand-primary/10 animate-glow-pulse" />
          )}
        </div>

        {/* Error Message */}
        {error && (
          <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
            <span className="inline-block w-1 h-1 rounded-full bg-red-400" />
            {error}
          </p>
        )}

        {/* Hint Message */}
        {hint && !error && (
          <p className="mt-1.5 text-xs text-text-muted">{hint}</p>
        )}
      </div>
    );
  },
);

BrandInput.displayName = "BrandInput";

export { BrandInput };

/**
 * Specialized Input Variants
 */

/**
 * Search Input
 */
export interface SearchInputProps extends BrandInputProps {
  onSearch?: (value: string) => void;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ onSearch, iconLeft, ...props }, ref) => {
    return (
      <BrandInput
        ref={ref}
        type="search"
        placeholder="Search..."
        iconLeft={
          iconLeft || (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          )
        }
        {...props}
      />
    );
  },
);

SearchInput.displayName = "SearchInput";

/**
 * Amount Input - for cryptocurrency amounts
 */
export interface AmountInputProps extends BrandInputProps {
  token?: string;
  balance?: string;
  onMaxClick?: () => void;
}

export const AmountInput = React.forwardRef<HTMLInputElement, AmountInputProps>(
  ({ token, balance, onMaxClick, label, ...props }, ref) => {
    return (
      <div className="w-full">
        {/* Balance Display */}
        {balance !== undefined && (
          <div className="flex justify-between items-center mb-2">
            {label && (
              <span className="text-sm text-text-secondary">{label}</span>
            )}
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-muted">
                Balance: {balance}
              </span>
              {onMaxClick && (
                <button
                  type="button"
                  onClick={onMaxClick}
                  className="text-xs text-brand-primary hover:text-brand-primary-light transition-colors"
                >
                  MAX
                </button>
              )}
            </div>
          </div>
        )}

        <BrandInput
          ref={ref}
          type="number"
          step="any"
          iconRight={
            token ? (
              <span className="text-sm font-semibold text-text-primary">
                {token}
              </span>
            ) : undefined
          }
          {...props}
        />
      </div>
    );
  },
);

AmountInput.displayName = "AmountInput";
